// ── Nav Preview (extracted from NavEditor) ──────────────────

import { X } from "lucide-react";

export interface AnnouncementConfig {
  enabled: boolean;
  message: string;
  linkUrl?: string;
  linkLabel?: string;
  dismissible: boolean;
  style: "primary" | "dark" | "accent";
}

export interface NavPreviewProps {
  items: { key: string; label: string; primary: boolean }[];
  headerBehavior: "static" | "fixed" | "sticky";
  logoUrl: string | null;
  primaryColor: string;
  tenantName: string;
  domain: string;
  announcementConfig?: AnnouncementConfig | null;
}

export function readableText(hex: string): string {
  const h = hex.trim().replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(h)) return "#ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
}

const ANNOUNCEMENT_STYLES: Record<
  AnnouncementConfig["style"],
  { bg: string; text: string }
> = {
  primary: { bg: "var(--tenant-primary, #2663C7)", text: "#ffffff" },
  dark: { bg: "#1a1a1a", text: "#ffffff" },
  accent: { bg: "var(--tenant-tertiary, #1e3a5f)", text: "#ffffff" },
};

function AnnouncementBarPreview({
  config,
  primaryColor,
}: {
  config: AnnouncementConfig;
  primaryColor: string;
}) {
  if (!config.enabled || !config.message?.trim()) return null;

  const styleKey = config.style || "primary";
  const colors = ANNOUNCEMENT_STYLES[styleKey] ?? ANNOUNCEMENT_STYLES.primary;
  // For "primary", use the actual tenant primary color
  const bg = styleKey === "primary" ? primaryColor : colors.bg;
  const textColor =
    styleKey === "primary" ? readableText(primaryColor) : colors.text;

  return (
    <div
      className="flex items-center justify-center gap-2 px-3 py-1"
      style={{ backgroundColor: bg, color: textColor }}
    >
      <span className="flex-1 text-center text-[8px] font-medium leading-tight truncate">
        {config.message}
      </span>
      {config.linkUrl && config.linkLabel && (
        <span
          className="flex-none rounded px-1.5 py-0.5 text-[7px] font-bold"
          style={{
            backgroundColor: "rgba(255,255,255,0.2)",
            color: textColor,
          }}
        >
          {config.linkLabel}
        </span>
      )}
      {config.dismissible && (
        <X size={8} strokeWidth={2.5} className="flex-none opacity-60" />
      )}
    </div>
  );
}

export function NavPreview({
  items,
  headerBehavior,
  logoUrl,
  primaryColor,
  tenantName,
  domain,
  announcementConfig,
}: NavPreviewProps) {
  return (
    <aside className="lg:sticky lg:top-4">
      <div className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b px-4 py-3">
          <h3 className="text-[14px] font-semibold">Preview</h3>
          <p className="mt-0.5 text-[11.5px] text-ink-4">
            {headerBehavior === "fixed" || headerBehavior === "sticky" ? "Sticky header" : "Static header"} with{" "}
            {items.length} {items.length === 1 ? "link" : "links"}
          </p>
        </div>

        <div className="p-4">
          {/* Mini browser frame */}
          <div className="hair overflow-hidden rounded-lg border bg-paper">
            {/* Browser chrome */}
            <div className="hair-b border-b flex items-center gap-2 bg-paper-2 px-3 py-2">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
              </div>
              <div className="hair flex flex-1 items-center rounded border bg-paper-hi px-2 py-1">
                <span className="truncate font-mono text-[10.5px] text-ink-4">
                  {domain || "yoursite.com"}
                </span>
              </div>
            </div>

            {/* Announcement bar */}
            {announcementConfig && (
              <AnnouncementBarPreview
                config={announcementConfig}
                primaryColor={primaryColor}
              />
            )}

            {/* Nav bar */}
            <div
              className="flex items-center gap-3 border-b px-3 py-2"
              style={{
                borderBottomColor: "var(--color-ink-6)",
                position: headerBehavior === "fixed" || headerBehavior === "sticky" ? "sticky" : "static",
                top: 0,
                backgroundColor: "var(--color-paper)",
              }}
            >
              {/* Logo */}
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-5 w-auto max-w-[60px] object-contain"
                />
              ) : (
                <span className="text-[10px] font-semibold text-ink-3 truncate max-w-[60px]">
                  {tenantName}
                </span>
              )}

              {/* Links */}
              <div className="flex flex-1 items-center justify-end gap-2">
                {items.filter(i => !i.primary).map((item) => (
                  <span
                    key={item.key}
                    className="text-[9px] text-ink-3 font-medium"
                  >
                    {item.label || "Link"}
                  </span>
                ))}
                {items.filter(i => i.primary).map((item) => (
                  <span
                    key={item.key}
                    className="rounded px-2 py-0.5 text-[9px] font-semibold"
                    style={{
                      backgroundColor: primaryColor,
                      // The primary CTA button is white-on-primary by design
                      // (ctaColors.ts: the "primary" slot is isDark → text-white,
                      // and the live nav renders Bulma .button.is-primary white).
                      // readableText() here went dark on a light-gold primary and
                      // mismatched the actual site.
                      color: "#ffffff",
                    }}
                  >
                    {item.label || "Link"}
                  </span>
                ))}
              </div>
            </div>

            {/* Page body placeholder */}
            <div className="space-y-2 p-3">
              <div className="h-2 w-3/4 rounded bg-ink-5/20" />
              <div className="h-2 w-1/2 rounded bg-ink-5/15" />
              <div className="h-8 w-full rounded bg-ink-5/10" />
              <div className="h-2 w-2/3 rounded bg-ink-5/15" />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
